
import { io, Socket } from 'socket.io-client';

const WS_URL = import.meta.env.VITE_WS_URL;

const normalizeNativeitWsUrl = (url: string | undefined): string | undefined => {
  if (!url || typeof window === 'undefined') return url;

  const currentHost = window.location.hostname;
  const isNativeitHost = currentHost === 'www.nativeit-botds.it' || currentHost === 'nativeit-botds.it';
  if (!isNativeitHost) return url;

  try {
    const parsed = new URL(url);
    const wsHost = parsed.hostname;
    if (wsHost === 'www.nativeit-botds.it' || wsHost === 'nativeit-botds.it') {
      return 'https://api.nativeit-botds.it';
    }
    return url;
  } catch {
    return 'https://api.nativeit-botds.it';
  }
};

const deriveFallbackWsUrl = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;

  const hostname = window.location.hostname;
  if (hostname === 'www.nativeit-botds.it' || hostname === 'nativeit-botds.it') {
    return 'https://api.nativeit-botds.it';
  }

  const fromApiUrl = import.meta.env.VITE_API_URL;
  if (fromApiUrl && fromApiUrl !== 'undefined' && fromApiUrl !== 'null') {
    try {
      const parsed = new URL(fromApiUrl);
      return `${parsed.protocol}//${parsed.host}`;
    } catch {
      // ignore invalid API URL and fallback to current origin
    }
  }

  return window.location.origin;
};

// Helper function to validate WebSocket URL
const isValidWebSocketUrl = (url: string | undefined): boolean => {
  if (!url || typeof url !== 'string') return false;
  if (url === 'undefined' || url === 'null' || url.trim() === '') return false;
  // Check if URL contains 'undefined' as a substring (common when env var is not set)
  if (url.includes('undefined') || url.includes('null')) return false;
  try {
    const urlObj = new URL(url);
    // Only allow ws://, wss://, http://, https:// protocols
    const validProtocols = ['ws:', 'wss:', 'http:', 'https:'];
    if (!validProtocols.includes(urlObj.protocol)) return false;
    // Ensure hostname is not empty
    if (!urlObj.hostname || urlObj.hostname.trim() === '') return false;
    return true;
  } catch {
    return false;
  }
};

const normalizedWsUrl = normalizeNativeitWsUrl(WS_URL);
const RESOLVED_WS_URL = isValidWebSocketUrl(normalizedWsUrl) ? normalizedWsUrl : deriveFallbackWsUrl();

class WebSocketClient {
  private socket: Socket | null = null;
  private listeners: Map<string, Set<(data: any) => void>> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private enabled: boolean;

  constructor() {
    // Only enable WebSocket if URL is configured and valid
    this.enabled = isValidWebSocketUrl(RESOLVED_WS_URL);
  }

  connect() {
    // Don't connect if WebSocket is not enabled
    if (!this.enabled) {
      // Silent - don't log if intentionally disabled
      return;
    }

    if (this.socket?.connected) {
      return;
    }

    if (!RESOLVED_WS_URL || !isValidWebSocketUrl(RESOLVED_WS_URL)) {
      this.enabled = false;
      return;
    }

    try {
      this.socket = io(RESOLVED_WS_URL, {
        transports: ['websocket', 'polling'], // Fallback to polling if websocket fails
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 10000,
        autoConnect: true,
      });
    } catch (error) {
      // Silent error - WebSocket is optional
      this.enabled = false;
      return;
    }

    let hasLoggedError = false;

    this.socket.on('connect', () => {
      console.log('WebSocket connected');
      this.reconnectAttempts = 0;
      hasLoggedError = false;
      
      // Authenticate with JWT token
      (async () => {
        const { api } = await import('./api');
        const token = api.getToken();
        if (token) {
          this.socket?.emit('authenticate', { token });
        }
      })();
    });

    this.socket.on('authenticated', (data: { success: boolean; error?: string }) => {
      if (data.success) {
        console.log('WebSocket authenticated');
      } else {
        // Only log authentication errors once
        if (!hasLoggedError) {
          console.warn('WebSocket authentication failed:', data.error);
          hasLoggedError = true;
        }
      }
    });

    this.socket.on('disconnect', (reason) => {
      // Only log unexpected disconnects
      if (reason === 'io server disconnect') {
        console.log('WebSocket disconnected by server');
      }
      // Silent for other disconnects
    });

    this.socket.on('connect_error', (error) => {
      // Only log the first error to avoid spam
      if (this.reconnectAttempts === 0 && !hasLoggedError) {
        // Only log in development or if it's not a network error
        if (import.meta.env.DEV) {
          console.warn('WebSocket connection failed. Real-time updates disabled.', error.message);
        } else {
          // In production, silently disable WebSocket if connection fails
          console.debug('WebSocket connection failed. Real-time updates disabled.');
        }
        hasLoggedError = true;
      }
      this.reconnectAttempts++;
      
      // Stop trying after max attempts and disable silently
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        this.enabled = false;
        this.disconnect();
        // Clear any pending reconnection attempts
        if (this.socket) {
          this.socket.removeAllListeners();
          this.socket = null;
        }
      }
    });

    // Forward all events to registered listeners
    this.socket.onAny((event, data) => {
      const listeners = this.listeners.get(event);
      if (listeners) {
        listeners.forEach(listener => listener(data));
      }
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.listeners.clear();
  }

  on(event: string, callback: (data: any) => void) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);

    // If socket is connected, also register with socket.io
    if (this.socket?.connected) {
      this.socket.on(event, callback);
    }

    return () => this.off(event, callback);
  }

  off(event: string, callback: (data: any) => void) {
    const listeners = this.listeners.get(event);
    if (listeners) {
      listeners.delete(callback);
    }
    this.socket?.off(event, callback);
  }

  emit(event: string, data?: any) {
    if (this.socket?.connected) {
      this.socket.emit(event, data);
    } else {
      // Silent - WebSocket is optional, don't spam console
      // Only log in development
      if (import.meta.env.DEV) {
        console.debug('WebSocket not connected, cannot emit:', event);
      }
    }
  }

  joinUser(userId: string) {
    this.emit('join:user', userId);
  }

  joinServer(serverId: string) {
    this.emit('join:server', serverId);
  }

  leaveServer(serverId: string) {
    this.emit('leave:server', serverId);
  }

  isConnected(): boolean {
    if (!this.enabled) return false;
    return this.socket?.connected || false;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  disable(): void {
    this.enabled = false;
    this.disconnect();
  }
}

export const wsClient = new WebSocketClient();

// Auto-connect on import only if enabled and in browser
// Only connect if user is authenticated (token exists) and URL is valid
if (typeof window !== 'undefined') {
  // Double-check that URL is valid before enabling
  if (!isValidWebSocketUrl(RESOLVED_WS_URL)) {
    // Silently disable if URL is invalid - don't log to avoid console spam
    wsClient.disable();
  } else if (wsClient.isEnabled()) {
    // Check if user is authenticated before connecting
    const checkAndConnect = () => {
      // Verify URL is still valid before connecting
      if (!isValidWebSocketUrl(RESOLVED_WS_URL)) {
        wsClient.disable();
        return;
      }
      (async () => {
        const { api } = await import('./api');
        const token = api.getToken();
        if (token && wsClient.isEnabled()) {
          // Delay connection slightly to avoid blocking initial page load
          setTimeout(() => {
            if (wsClient.isEnabled()) {
              wsClient.connect();
            }
          }, 1000);
        }
      })();
    };

    // Try immediately
    checkAndConnect();

    // Also listen for auth events
    window.addEventListener('storage', (e) => {
      if (e.key === 'auth_token' && wsClient.isEnabled()) {
        checkAndConnect();
      }
    });
  }
}
